import { defineStore } from 'pinia';
import API from '@/shared/helpers/API.js';
import {useUserStore} from "@/console-new/stores";
import {useLanguageStore, useScriptStore} from "@/shared/stores";
import {getLocalDateFromServer} from "@/shared/helpers/dates";

export const useCardsStore = defineStore('cards', {
	state: () => ({
		showingCards: false,
		activeCardId: null,
		businessCards: null,
		businessCardholders: null,
		totalCardsPhysical: 0,
		totalCardsVirtual: 0,
		availableColors: {
			purple: "#8F4DFE",
			fucsia: "#FF3D00",
			orange: "#FC7502",
			yellow: "#eeba00",
			green: "#77b80b",
			aqua: "#1fc59f",
			lblue: "#02bde7",
			blue: "#0752A3",
			black: "#000000",
		},
		stripe: {
			instance: null,
			elements: null,
			nonce: null,
			ephemeralKey: null,
			detailsCardId: null,
		},
		cardTransactions: {
			loading: false,
			data: {},  // keyed by card_id
		},
		cardTransactionsTable: null,
		cardTransactionsTableTotal: 0,
		issuingBalance: 0,
		issuingPending: 0,
		loadingBalance: true,
		isCarouselScrolling: false,
		forceRefresh: 0
	}),
	getters: {
		activeCardTransactions() {
			if (!this.activeCardId || !this.cardTransactions.data[this.activeCardId]) {
				return null;
			}
			return this.cardTransactions.data[this.activeCardId];
		}
	},
	actions: {
		getBusinessCards(callback = null, force = false) {
			if(!force && this.businessCards != null) { callback && callback(this.businessCards); return; }

			let userStore = useUserStore();
			if(!userStore.currBusinessId) { return; }
			API.init().getCards(userStore.currBusinessId, response => {
				if (response && response.result === "OK") {
					if(response.cards && response.cards.length > 0) {
						this.businessCards = this.normalizeCards(response.cards);
						// Only set activeCardId to first card if none is selected
						// or if the previously selected card no longer exists
						if(this.businessCards.length > 0) {
							if (!this.activeCardId || !this.businessCards.find(card => card.card_id === this.activeCardId)) {
								this.changeCard(this.businessCards[0].card_id);
							}
						}
					} else {
						this.businessCards = [];
						this.activeCardId = null;
					}
					if(response.cardholders && response.cardholders.length > 0) {
						this.businessCardholders = response.cardholders || [];
					} else {
						this.businessCardholders = [];
					}
					callback && callback(this.businessCards);
				}
			});
		},
		normalizeCards(cards) {
			this.totalCardsPhysical = cards.filter(card => card.type === 'physical').length;
			this.totalCardsVirtual = cards.filter(card => card.type === 'virtual').length;
			return cards.filter(card => card.deleted === 0);
		},
		changeCard(cardId) {
			this.activeCardId = cardId;
			this.hideCardDetails();
		},
		getCardById(cardId) {
			if(!this.businessCards || this.businessCards.length === 0) { return null; }
			return this.businessCards.find(card => card.card_id === cardId) || null;
		},
		normalizeTransactionData(data) {
			// Normalize authorizations
			if (data.authorizations) {
				data.authorizations = data.authorizations.map(auth => ({
					...auth,
					date: getLocalDateFromServer(auth.date)
				}));
			}
			
			// Normalize transactions
			if (data.transactions) {
				data.transactions = data.transactions.map(txn => ({
					...txn,
					date: getLocalDateFromServer(txn.date)
				}));
			}
			
			return data;
		},
		resetData() {
			this.activeCardId = null;
			this.businessCards = null;
			this.businessCardholders = null;
			this.stripe.instance = null;
			this.stripe.elements = null;
			this.stripe.ephemeralKey = null;
			this.stripe.nonce = null;
			this.stripe.detailsCardId = null;
			this.cardTransactions.loading = false;
			this.cardTransactions.data = {};
			this.cardTransactionsTable = null;
			this.cardTransactionsTableTotal = 0;
			this.issuingBalance = 0;
			this.issuingPending = 0;
			this.loadingBalance = true;
			this.isCarouselScrolling = false;
			this.forceRefresh = 0;
			this.showingCards = false;
		},
		getStripeSecretKey(card, otpCode, callback = null) {

			const userStore = useUserStore();
			useScriptStore().loadScript("stripe", () => {
				if(!this.stripe.instance) {
					this.stripe.instance = Stripe(STRIPE_PUBLIC_KEY_2, {
						stripeAccount: userStore.currBusiness.stripe_account,
					});
				}
				if(!this.stripe.elements) {
					this.stripe.elements = this.stripe.instance.elements({
						fonts: [
							{
								cssSrc: 'https://fonts.googleapis.com/css2?family=Montserrat:wght@500&display=swap',
							},
						]
					});
				}

				new Promise(async (resolve, reject) => {

					const nonceResult = await this.stripe.instance.createEphemeralKeyNonce({
						issuingCard: card.stripe_id,
					});
					const nonce = nonceResult.nonce;

					API.init().getCardsEphemeralKey(userStore.currBusinessId, card.card_id, nonce, otpCode, (response) => {
						if (response && response.result === "OK") {
							if(response.ephemeral_key) {
								this.stripe.ephemeralKey = response.ephemeral_key;
								this.stripe.nonce = nonce;
								this.stripe.detailsCardId = card.card_id;
								resolve(response.ephemeral_key);
							} else {
								reject("No ephemeral key received");
							}
						} else {
							reject("Error getting ephemeral key: " + (response ? response.details : "Unknown error"));
						}
					});

				}).then((ephemeralKey) => {
					callback && callback(ephemeralKey);
				}).catch((error) => {
					console.error("Error getting Stripe secret key:", error);
					callback && callback(null, error);
				});

			});

		},
		hideCardDetails() {
			this.stripe.ephemeralKey = null;
			this.stripe.nonce = null;
			this.stripe.detailsCardId = null;
		},
		getCardTransactions(cardId, startDate = null, callback = null, force = false) {
			// Create cache key that includes the start date
			const cacheKey = startDate ? `${cardId}_${startDate}` : `${cardId}_all`;
			
			// Don't fetch if we already have data for this card and date range and not forcing
			if (!force && this.cardTransactions.data[cacheKey]) {
				callback && callback(this.cardTransactions.data[cacheKey]);
				return;
			}

			const userStore = useUserStore();
			if (!userStore.currBusinessId) { return; }

			this.cardTransactions.loading = true;
			
			API.init().getCardTransactions(userStore.currBusinessId, cardId, startDate, response => {
				this.cardTransactions.loading = false;
				
				if (response && response.result === "OK") {
					// Normalize the data (convert UTC dates to local)
					const normalizedData = this.normalizeTransactionData({
						authorizations: response.authorizations || [],
						transactions: response.transactions || []
					});
					
					// Store the transaction data for this card
					this.cardTransactions.data[cacheKey] = {
						...normalizedData,
						lastFetched: new Date(),
						startDate: startDate
					};
					
					callback && callback(this.cardTransactions.data[cacheKey]);
				} else {
					// On error, store empty data
					this.cardTransactions.data[cacheKey] = {
						authorizations: [],
						transactions: [],
						lastFetched: new Date(),
						startDate: startDate,
						error: true
					};
					
					callback && callback(this.cardTransactions.data[cacheKey]);
				}
			});
		},
		getFilteredCardTransactions(businessId, cardId, filters, callback = null) {
			const userStore = useUserStore();
			if (!userStore.currBusinessId) { return; }

			API.init().getFilteredCardTransactions(userStore.currBusinessId, cardId, filters, response => {
				if (response && response.result === "OK") {
					// Normalize the transaction data (convert UTC dates to local)
					const normalizedTransactions = (response.transactions || []).map(txn => ({
						...txn,
						date: getLocalDateFromServer(txn.date)
					}));
					
					this.cardTransactionsTable = normalizedTransactions;
					this.cardTransactionsTableTotal = response.total || 0;
					callback && callback(response);
				} else {
					this.cardTransactionsTable = [];
					this.cardTransactionsTableTotal = 0;
					callback && callback(response);
				}
			});
		},
		getFundingInstructions(callback = null) {
			const userStore = useUserStore();
			if (!userStore.currBusinessId) { return; }

			API.init().getFundingInstructions(userStore.currBusinessId, response => {
				callback && callback(response);
			});
		},
		
		getIssuingBalance(callback = null) {
			const userStore = useUserStore();
			if (!userStore.currBusinessId) {
				this.loadingBalance = false;
				callback && callback(null);
				return;
			}

			this.loadingBalance = true;
			API.init().getIssuingBalance(userStore.currBusinessId, response => {
				this.loadingBalance = false;
				if (response && response.result === 'OK' && response.balance !== undefined) {
					this.issuingBalance = response.balance;
					this.issuingPending = response.pending || 0;
				} else {
					this.issuingBalance = 0;
					this.issuingPending = 0;
				}
				callback && callback(response);
			});
		},
		triggerRefresh() {
			this.forceRefresh++;
		},
		cancelCard(cardId, otpCode, callback = null) {
			const userStore = useUserStore();
			if (!userStore.currBusinessId) { 
				callback && callback({ result: "KO", details: "no_business" }); 
				return; 
			}

			API.init().cancelCard(userStore.currBusinessId, cardId, otpCode, response => {
				if (response && response.result === "OK") {
					// Remove the card from the local state
					if (this.businessCards) {
						const cardIndex = this.businessCards.findIndex(card => card.card_id === cardId);
						if (cardIndex !== -1) {
							this.businessCards.splice(cardIndex, 1);
							// If this was the active card, select another one
							if (this.activeCardId === cardId) {
								if (this.businessCards.length > 0) {
									this.changeCard(this.businessCards[0].card_id);
								} else {
									this.activeCardId = null;
								}
							}
						}
					}
					// Trigger refresh to update any dependent data
					this.triggerRefresh();
				}
				callback && callback(response);
			});
		}
	}
});
